/**
 * The id-token claims this app reads — a subset chosen by the UI, not exhaustive.
 *
 * - Not in `@keboola/api-client`: apps-api's spec types the token as an opaque string.
 * - All optional and unvalidated: a claim the pool stops sending must read as absent, not throw.
 */
export type TokenClaims = {
  name?: string;
  email?: string;
  /** Comma-separated vendor ids; `_v`-prefixed ones are pending and never user-selectable. */
  profile?: string;
  'custom:isAdmin'?: string;
  exp?: number;
};

/**
 * Prefix of a pending self-registered vendor's temporary id. Never selectable: requests would
 * scope to a vendor that does not exist yet.
 */
const PENDING_VENDOR_PREFIX = '_v';

/** What the app derives from the id token — read, never verified (that's the API's job). */
export type Identity = {
  name: string | null;
  email: string | null;
  /** Approved, selectable vendors; pending (`_v…`) ids are filtered out. */
  vendors: string[];
  isAdmin: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (claims: Record<string, unknown>, key: string): string | undefined =>
  typeof claims[key] === 'string' ? claims[key] : undefined;

/**
 * Decodes a JWT payload without verifying it; `{}` for anything unparseable, never throws.
 *
 * - Bytes go through `TextDecoder`: the payload is UTF-8, and `atob` alone mangles multi-byte chars.
 */
export const decodeClaims = (token: string): TokenClaims => {
  const payload = token.split('.')[1];
  if (payload === undefined || payload === '') return {};

  try {
    const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (!isRecord(claims)) return {};

    // Per claim: a non-string `profile` would otherwise crash `readVendors`' `.split()` mid-render.
    return {
      name: readString(claims, 'name'),
      email: readString(claims, 'email'),
      profile: readString(claims, 'profile'),
      'custom:isAdmin': readString(claims, 'custom:isAdmin'),
      exp: typeof claims.exp === 'number' ? claims.exp : undefined,
    };
  } catch (error) {
    console.warn(
      'Could not decode the Developer Portal id token; treating it as no identity.',
      error,
    );
    return {};
  }
};

/**
 * Parses the comma-separated `profile` claim, dropping pending `_v` ids — a pending-only user has
 * no vendors, which drives the "select or create a vendor" gate.
 */
export const readVendors = (profile: string | undefined): string[] => {
  if (profile === undefined || profile === '') return [];
  return profile
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id !== '' && !id.startsWith(PENDING_VENDOR_PREFIX));
};

/** Admin is `custom:isAdmin === '1'` exactly; anything else, including absent, is not. */
export const readIsAdmin = (claims: TokenClaims): boolean => claims['custom:isAdmin'] === '1';

export const readIdentity = (token: string): Identity => {
  const claims = decodeClaims(token);
  return {
    name: claims.name ?? null,
    email: claims.email ?? null,
    vendors: readVendors(claims.profile),
    isAdmin: readIsAdmin(claims),
  };
};

/**
 * Picks the vendor to scope requests to after a login or token rotation.
 *
 * - Keeps `current` if it is still in `vendors`, else the first one.
 * - Client state only, so a rotation that drops the current vendor must not leave the app on it.
 */
export const selectCurrentVendor = (vendors: string[], current: string | null): string | null => {
  if (current !== null && vendors.includes(current)) return current;
  return vendors[0] ?? null;
};
